import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  useTemperatureControlSearchParams,
  type FiltersTemperatureControl,
} from 'hooks/store/temperature-control/use-temperature-control-search-params';

import { useCompanyOptions } from 'hooks/api/options/general/use-company-options';
import { CompanySiteTypeEnum } from 'constants/CompanySiteType';

import { formSchema, type FormSchema } from './form-schema';
import { createFilterParams } from './utils';

import { Form, FormField } from 'components/ui/form';
import { MultiSelectGs, InputGs, DateRangePickerGs } from 'components/ui/Forms';

import { Button } from 'components/ui/ButtonGs';

type Ref = {
  resetFilters: () => void;
  getFormData: () => Record<string, unknown>;
};

type Props = {
  onSubmitFilters: (filters: FiltersTemperatureControl) => void;
  onResetFilters: () => void;
  onClickRequestReport?: () => void;
};

export type TemperatureControlFiltersRef = Ref;

const SELECTED_BADGE_CLASS_NAME =
  'md:max-w-[200px] lg:max-w-[180px] xl:max-w-[150px]';

const rangeTemperatureOptions = [
  { label: 'Menor que 4°C', value: '-999,3' },
  { label: 'Entre 4°C e 10°C', value: '4,10' },
  { label: 'Maior que 10°C', value: '11,999' },
];

export const TemperatureControlFilter = React.forwardRef<Ref, Props>(
  ({ onSubmitFilters, onResetFilters }, ref) => {
    const [formHydrated, setFormHydrated] = React.useState(false);

    const { filters } = useTemperatureControlSearchParams({
      persisted: true,
    });

    const companyOptions = useCompanyOptions({
      labelType: 'trade-name',
      siteType: CompanySiteTypeEnum.DISTRIBUTION_CENTER,
    });

    const form = useForm<FormSchema>({
      resolver: zodResolver(formSchema),
    });

    const hydrateForm = React.useCallback(() => {
      const {
        companyId,
        productSku,
        productDescription,
        equipmentCode,
        productBatch,
        temperature,
        eventDateFrom,
        eventDateTo,
      } = filters;

      form.setValue('companyId', companyId);
      form.setValue('productSku', productSku);
      form.setValue('productDescription', productDescription);
      form.setValue('equipmentCode', equipmentCode);
      form.setValue('productBatch', productBatch);
      form.setValue(
        'temperature',
        Array.isArray(temperature)
          ? temperature
          : temperature
            ? [temperature]
            : undefined
      );
      form.setValue('eventDate', {
        from: eventDateFrom ? new Date(eventDateFrom) : undefined,
        to: eventDateTo ? new Date(eventDateTo) : undefined,
      });

      setFormHydrated(true);
    }, [filters, form]);

    const resetForm = React.useCallback(() => {
      form.reset(
        {
          companyId: undefined,
          productSku: '',
          productDescription: '',
          equipmentCode: '',
          productBatch: '',
          eventDate: undefined,
          temperature: undefined,
        },
        { keepValues: false }
      );

      onResetFilters();
    }, [form, onResetFilters]);

    const onFormSubmit = React.useCallback(
      (formData: FormSchema) => {
        const filters = createFilterParams(formData);
        return onSubmitFilters(filters);
      },
      [onSubmitFilters]
    );

    React.useEffect(() => {
      if (!formHydrated) {
        hydrateForm();
      }
    }, [formHydrated, hydrateForm]);

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFormSubmit)} className="w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
            <FormField
              control={form.control}
              name="productBatch"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Lote" />
              )}
            />
            <FormField
              control={form.control}
              name="productSku"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Cód. Produto" />
              )}
            />
            <FormField
              control={form.control}
              name="productDescription"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Descrição Produto" />
              )}
            />
            <FormField
              control={form.control}
              name="equipmentCode"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Cód. equipamento" />
              )}
            />

            <FormField
              control={form.control}
              name="companyId"
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label="Filial"
                  maxCount={1}
                  options={companyOptions.multiSelectOptions}
                  classNames={{
                    selectedBadge: SELECTED_BADGE_CLASS_NAME,
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name="eventDate"
              render={({ field }) => (
                <DateRangePickerGs
                  selected={{
                    from: field.value?.from,
                    to: field.value?.to,
                  }}
                  onSelect={field.onChange}
                  label="Data início/Fim"
                  clearable
                />
              )}
            />
            <FormField
              control={form.control}
              name="temperature"
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label="Faixa de Temperatura"
                  options={rangeTemperatureOptions}
                  maxCount={3}
                />
              )}
            />
          </div>

          <div className="flex items-center mt-6 gap-4">
            <Button.Root
              type="button"
              size="sm"
              variant="secondary"
              onClick={resetForm}
            >
              Limpar filtros
            </Button.Root>

            <Button.Root type="submit" size="sm" variant="primary">
              Filtrar
            </Button.Root>
          </div>
        </form>
      </Form>
    );
  }
);

TemperatureControlFilter.displayName = 'TemperatureControlFilter';
